import { makeStyles } from '@material-ui/core';
import FundIcon from '@material-ui/icons/AccountBalanceWallet';
import React, { useMemo, useEffect, useState } from 'react';
import {
  Datagrid,
  List,
  ReferenceManyField,
  Responsive,
  TextInput,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  Pagination,
  useGetList,
  Loading,
  useQuery,
  useListContext
} from 'react-admin';
import omit from 'lodash/omit';

import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import StatusSelectField from '../../core/components/StatusSelectField';
import AutocompleteInput from '../../core/components/forked/input/AutocompleteInput';
import ListTitleBar from './fundimport/ExcelFundImportTitleBarWithDropbox';

import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import { fundStatus } from './FundLookups';
import InvestorFundDatagrid from './InvestorFundDatagrid';
import TagsField from '../../core/components/TagsField';
import TagFilter from '../../core/components/inputs/TagFilter';

const useStyles = makeStyles({
  expandOnlyDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      }
    }
  },
  expandDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      },
      '& th:nth-of-type(2)': {
        width: '24px'
      }
    }
  },
  expandIconCell: {
    width: '14px',
    '& div': {
      width: '14px',
      padding: '0'
    },
    '& div:hover ': {
      backgroundColor: 'transparent'
    }
  },
  statusColumnStyle: {
    width: '68px'
  },
  root: {
    marginRight: 'auto'
  },
  autocompleteSuggestions: {
    zIndex: 3010,
    backgroundColor: '#ffffff'
  }
});

const useshowStyles = makeStyles({
  root: {
    padding: '16px 16px 10px 16px'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();
  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const FundFilters = ({
  filterValues,
  setAppliedFilters,
  fundTags,
  selectedTags,
  setSelectedTags,
  fundtagsCount,
  ...props
}) => {
  const { data } = useQuery({
    type: 'getList',
    resource: 'fundfamilies',
    payload: {
      orderDirection: 'ASC'
    }
  });

  const handleTagFilterChange = e => {
    setSelectedTags(e);
  };

  useEffect(() => {
    // Hack to reset filter when a tag is deleted while it was selected previously for filtering the list
    filterValues.fundTagId?.forEach(tagId => {
      const isTagInDB = Object.values(fundTags).some(
        tagChoice => tagChoice.id === tagId
      );
      if (fundTags.length === 0 || !isTagInDB) {
        delete filterValues.fundTagId;
        setSelectedTags([]);
      }
    });
    if (filterValues) {
      setAppliedFilters(filterValues);
      if (filterValues.fundTagId) {
        setSelectedTags(filterValues.fundTagId);
      }
    }
  }, [filterValues, fundTags, setAppliedFilters, setSelectedTags]);

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Fund Name"
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      {data && data.length > 0 && (
        <AutocompleteInput
          source="fundFamilyId"
          label="Fund Families"
          alwaysOn
          optionText="name"
          autoComplete="off"
          showArrowDown={true}
          clientSideFilterOnly={true}
          choices={data}
          emptyText="All Fund Families"
          placeholder="All Fund Families"
          id="autocomplete-input-fund-families"
          menuId="autocomplete-input-menu-fund-families"
          getItemId={itemIndex =>
            `autocomplete-input-menu-item-fund-families-${itemIndex}`
          }
        />
      )}
      {fundtagsCount > 0 && (
        <TagFilter
          alwaysOn
          source="fundTagId"
          tagsList={Object.values(fundTags)}
          selectedTags={selectedTags}
          displayTagFilter={true}
          displayFundInvTagFilter={false}
          onChange={handleTagFilterChange}
          onChangeFundInv={() => null}
          isListFilter={true}
          selectLabel="Fund Tags"
          topLabel="Fund Tags"
          filterValues={filterValues}
        />
      )}
      <TextInput
        label="Fund ID"
        source="id"
        allowEmpty
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <SelectInput
        label="Status"
        source="status"
        choices={fundStatus}
        allowEmpty
      />
    </Filter>
  );
};

const FundShow = props => {
  const classes = useshowStyles();
  const { handleInvestorClick, permissions, ...rest } = props;
  return (
    <div className={classes.root}>
      <ReferenceManyField
        label=""
        reference="fundinvestors"
        target="fundId"
        sort={{ field: 'investorName', order: 'ASC' }}
        {...rest}
      >
        <InvestorFundDatagrid
          handleInvestorClick={handleInvestorClick}
          expand={true}
          permissions={permissions}
        />
      </ReferenceManyField>
    </div>
  );
};

const FundsTitle = props => {
  return (
    <ListTitleBar
      type="Funds"
      title="Create New Fund"
      createUrl="#/funds/create"
      icon={<FundIcon />}
      hasExportFundExcelPermission={props.hasExportFundExcelPermission}
      hasImportFundExcelPermission={props.hasImportFundExcelPermission}
      hasCreatePermission={props.hasCreatePermission}
      filters={props.filters}
      selectedIds={props.selectedIds}
      props={props}
    />
  );
};

const FundList = props => {
  const {
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    ...rest
  } = props;
  const classes = useStyles();
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleInvestorClick = fundInvestorId => {
    const investorId = fundInvestorId.split(':')[1];
    return `/investors/${investorId}`;
  };

  // Fetch all fundtags. If there is at least one, we need to display the "Fund Tags" column.
  const {
    data: fundTags,
    loading: fundTagLoading,
    total: fundtagsCount
  } = useGetList('fundtags', null, null);

  const { hasImportFundExcelPermission } = useMemo(() => {
    return {
      hasImportFundExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_FUND_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasExportFundExcelPermission } = useMemo(() => {
    return {
      hasExportFundExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_EXPORT_FUND')
        : false
    };
  }, [props.permissions]);

  // Wait for the GET /v1/fundtags response before displaying the grid.
  if (fundTagLoading) {
    return <Loading />;
  } else {
    return (
      <List
        {...omit(rest, ['userProfile'])}
        sort={{ field: 'name', order: 'ASC' }}
        exporter={false}
        title={
          <FundsTitle
            hasImportFundExcelPermission={hasImportFundExcelPermission}
            hasExportFundExcelPermission={hasExportFundExcelPermission}
            hasCreatePermission={hasCreatePermission}
            filters={appliedFilters}
            selectedIds={selectedIds}
          />
        }
        filters={
          <FundFilters
            setAppliedFilters={setAppliedFilters}
            fundTags={fundTags}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            fundtagsCount={fundtagsCount}
          />
        }
        filter={fundtagsCount > 0 && { returnFundTags: true }}
        perPage={20}
        pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
        bulkActionButtons={hasDeletePermission && <BulkActionDeleteButton />}
        actions={<Actions />}
        empty={<Empty />}
        syncWithLocation
      >
        <Responsive
          small={
            <SimpleList
              primaryText={record => (record?.name ? record.name : '')}
              secondaryText={record => (record?.id ? record.id : '')}
              tertiaryText={() => ''}
            />
          }
          medium={
            <DatagridWithListContext
              hasDeletePermission={hasDeletePermission}
              handleInvestorClick={handleInvestorClick}
              fundtagsCount={fundtagsCount}
              permissions={permissions}
              classes={classes}
              setSelectedIds={setSelectedIds}
              {...rest}
            />
          }
        />
      </List>
    );
  }
};

const DatagridWithListContext = props => {
  const {
    hasDeletePermission,
    handleInvestorClick,
    fundtagsCount,
    permissions,
    classes,
    setSelectedIds,
    ...dataGridProps
  } = props;
  const { selectedIds } = useListContext(props);

  useEffect(() => {
    setSelectedIds(selectedIds);
  }, [selectedIds, setSelectedIds]);

  return (
    <>
      <Datagrid
        {...dataGridProps}
        classes={{
          thead: hasDeletePermission
            ? classes.expandDataGridHead
            : classes.expandOnlyDataGridHead,
          expandIconCell: classes.expandIconCell
        }}
        expand={
          <FundShow
            handleInvestorClick={handleInvestorClick}
            permissions={permissions}
          />
        }
      >
        <LinkField label="Fund Name" source="name" id="fund name" />
        <TextField label="A.K.A." source="aka" id="fund aka" />
        <TextField label="Fund ID" source="id" id="fund id" />
        <TextField
          label="Fund Contact Email"
          source="emailAddresses[0]"
          sortable={false}
          id="fund email"
        />
        {fundtagsCount > 0 && (
          <TagsField
            label="Fund Tags"
            source="fundtags"
            tagType="fundTags"
            sortable={false}
          />
        )}
        <StatusSelectField
          label="Status"
          source="status"
          choices={fundStatus}
          id="fund status"
          headerClassName={classes.statusColumnStyle}
        />
      </Datagrid>
    </>
  );
};

export default FundList;
